// The email module's pages — Important, Drafts, Trash, Spam, Scheduled and the rest — each have
// an EMPTY state and a listing (busy-my-web#304 – #308, Figma 40010488-211833 … 40010531-249577).
// The listing is the shared row (EntityPlaceholders → CollapsedMode); this module is the reading
// of the other half: what each folder says when it has nothing to show, and when that is what a
// page should be showing. Plain data so the copy can be checked without mounting a view.

// Icon names are from the pack (src/assets/icon-packs); every one here exists in `default`.
//
// The five folders the 2026-09-01 batch draws (busy-my-web#319) say ONLY a line — no heading over
// it — and the line is the frame's own words, which explain the folder rather than merely reporting
// that it is empty. `title` is therefore empty on those five and the heading is not drawn; the
// folders the batch has no frame for keep the heading they were given in #304 – #308.
//
// Those same five carry `illustration`, the frame's isometric artwork (busy-my-web#344, Fatemeh's
// exports pulled from Figma file Os2uqvgQeMQBhMEhrWiy33) — an image name resolved by
// EmailEmptyState against src/assets/illustrations, not an icon-pack name. Draft and Scheduled
// point at the same file: the frames export identical artwork ("Box with a note") for both.
export const EMPTY_STATES = {
  inbox: {
    icon: 'direct-inbox',
    title: 'Your inbox is empty',
    text: 'New mail lands here as it arrives.'
  },
  waitingOn: {
    icon: 'waiting-on',
    title: 'Nothing waiting on a reply',
    text: 'Emails you are waiting to hear back on appear here.'
  },
  // Figma 40010488-211833. Illustration: "Box is overturned".
  important: {
    icon: 'important',
    illustration: 'box-is-overturned',
    title: '',
    text:
      'Importance lets you give messages a special status, making them easier to find. ' +
      'To mark a message as important, hover over it and click the star icon next to the ' +
      'message or conversation.'
  },
  overdue: {
    icon: 'notification-off',
    title: 'Nothing overdue',
    text: 'Emails whose due date has passed appear here.'
  },
  dueSoon: {
    icon: 'notification',
    title: 'Nothing due soon',
    text: 'Emails coming up on a due date appear here.'
  },
  archived: {
    icon: 'archive',
    title: 'No archived emails',
    text: 'Emails you archive are kept here, out of the inbox.'
  },
  sent: {
    icon: 'sent',
    title: 'No sent emails',
    text: 'Emails you send appear here.'
  },
  // Figma 40010508-221517. Illustration: "Box with a note".
  draft: {
    icon: 'draft',
    illustration: 'box-with-a-note',
    title: '',
    text:
      "You don't have any saved drafts. " +
      "Saving a draft allows you to keep a message you aren't ready to send yet."
  },
  // Figma 40010510-236283. Illustration: "Box with secret" — two Figma layers (box + overlay),
  // composed into one file at their recorded insets (see #344 dev notes).
  bin: {
    icon: 'bin',
    illustration: 'box-with-secret',
    title: '',
    text: 'Messages that have been in Trash more than 30 days will be automatically deleted.'
  },
  // Figma 40010528-246789. Illustration: "Box with handles".
  spam: {
    icon: 'spam',
    illustration: 'box-with-handles',
    title: '',
    text: 'Hooray, no spam here!'
  },
  // Figma 40010531-248855. Illustration: "Box with a note" (same artwork as Draft's export).
  scheduled: {
    icon: 'past',
    illustration: 'box-with-a-note',
    title: '',
    text: 'Messages in Scheduled will be sent at their scheduled time.'
  }
}

// A search that found nothing is the same on every page (the wording Inbox.vue already used).
export const SEARCH_EMPTY = {
  icon: 'global-search',
  title: 'No emails found',
  text: 'Try a different search or clear the filter.'
}

// The frames call the bin "Trash"; the route and the store keep `bin`.
export const PAGE_TITLE = {
  inbox: 'Inbox',
  waitingOn: 'Waiting on',
  important: 'Important',
  overdue: 'Overdue',
  dueSoon: 'Due soon',
  archived: 'Archived',
  sent: 'Sent',
  draft: 'Drafts',
  bin: 'Trash',
  spam: 'Spam',
  scheduled: 'Scheduled'
}

export const pageTitle = (subMenu) => PAGE_TITLE[subMenu] || PAGE_TITLE.inbox

// What an empty page says. A search term wins over the folder: "No emails found" is about the
// search, not the folder, and clearing the term brings the folder's own line back.
export const emptyState = (subMenu, { search = '' } = {}) => {
  if (String(search ?? '').trim()) return SEARCH_EMPTY
  return EMPTY_STATES[subMenu] || EMPTY_STATES.inbox
}

// Whether the empty state is what the page should show right now. Never while the list is still
// loading (a flash of "empty" over a fetch is a lie), never behind the Google permission gate, and
// only when there is nothing on the page.
export const showEmptyState = ({ loading = false, count = 0, needsPermission = false } = {}) =>
  !loading && !needsPermission && Number(count || 0) === 0

// ── Trash (busy-my-web#306) ──────────────────────────────────────────────────────────────────
// "Clear trash" removes everything in the folder for good; the confirm names the count so the
// user knows what they are agreeing to.
export const CLEAR_TRASH_TITLE = 'Clear trash'
export const clearTrashMessage = (count) => {
  const n = Number(count || 0)
  if (n <= 0) return 'Trash is already empty.'
  return n === 1
    ? 'Permanently delete the 1 email in Trash? This cannot be undone.'
    : `Permanently delete all ${n} emails in Trash? This cannot be undone.`
}

// ── Scheduled (busy-my-web#308) ──────────────────────────────────────────────────────────────
// The BFF's schedule record is `{ id, subject, sendAt, recipients }` (busy-main-bff#14). The row
// wants the listing's shape: participants (who it goes TO), a headline, and a date to stamp.
const parseAddress = (raw) => {
  if (!raw) return null
  if (typeof raw === 'object') {
    const email = raw.email || raw.emailAddress || raw.address || ''
    const name = raw.name || email
    return name || email ? { id: raw.id || null, name, email } : null
  }
  const s = String(raw).trim()
  if (!s) return null
  const m = s.match(/^(.*?)\s*<([^>]+)>$/)
  return m ? { id: null, name: m[1].trim() || m[2].trim(), email: m[2].trim() } : { id: null, name: s, email: s }
}

export const scheduledRecipients = (recipients) => {
  const list = Array.isArray(recipients)
    ? recipients
    : String(recipients || '').split(/[;,]/)
  return list.map(parseAddress).filter(Boolean)
}

export const scheduledEntity = (record) => ({
  id: record?.id || '',
  headLine: record?.subject || '(No subject)',
  participants: scheduledRecipients(record?.recipients),
  receivedUtc: record?.sendAt || record?.sendAtUtc || null,
  // A scheduled mail is the user's own — never "unread".
  seen: true,
  isCollapsed: true,
  isScheduled: true
})

// Soonest first: that is how the BFF orders them and how the page keeps them after a cancel.
export const sortScheduled = (records) =>
  [...(Array.isArray(records) ? records : [])].sort(
    (a, b) => new Date(a?.sendAt || a?.sendAtUtc || 0) - new Date(b?.sendAt || b?.sendAtUtc || 0)
  )

export default emptyState
